import React, { useState } from 'react';
import {
  ChevronRight,
  Megaphone,
  Headphones,
  TrendingUp,
  Calculator,
  Search,
  Keyboard,
  Box,
  MessageCircle,
  X,
  HelpCircle,
  Lightbulb,
} from 'lucide-react';
import { Breadcrumb, SectionBand, CtaBand, ProductLinks, PRODUCT_LOGIN } from './DemoSiteLayout';
import { T, SURFACE, SHADOW, Hero, DemoLink, IconTile, StepMarker, CenterHead, StatStrip, StatementBand, Chip } from './siteUI';

/* ---------- data ---------- */

const HERO_EMPLOYEES = [
  { name: 'Marketing Assistant', status: 'Active', tint: 'bg-violet-100 text-violet-600' },
  { name: 'Marketing Manager', status: 'Active', tint: 'bg-teal-100 text-teal-600' },
  { name: 'Research Analyst', status: 'Active', tint: 'bg-sky-100 text-sky-600' },
  { name: 'Support Specialist', status: 'Working on 3 tasks', tint: 'bg-orange-100 text-orange-500' },
];

const HOW_STEPS = [
  { n: 1, title: 'Hire', desc: 'Choose from AI employees or create your own.' },
  { n: 2, title: 'Knowledge & Connections', desc: 'Connect tools, apps, and systems.' },
  { n: 3, title: 'Skills', desc: 'Define what your AI employees can do.' },
  { n: 4, title: 'Assign Job', desc: 'Delegate tasks with context and goals.' },
  { n: 5, title: 'Job Done', desc: 'AI gets the work done. You review results.' },
];

const FILTERS = ['All', 'Marketing', 'Support', 'Operations', 'Sales', 'Finance'];

const EMPLOYEES = [
  {
    name: 'Marketing Assistant',
    desc: 'Drafts content, schedules campaigns, and keeps social channels active.',
    icon: Megaphone, tint: 'bg-violet-50 text-violet-600', cat: 'Marketing',
    skills: ['Copywriting', 'Scheduling', 'Analytics'],
  },
  {
    name: 'Support Specialist',
    desc: 'Triages tickets, answers common questions, and escalates what needs a human.',
    icon: Headphones, tint: 'bg-teal-50 text-teal-600', cat: 'Support',
    skills: ['Ticket triage', 'Knowledge base', 'Escalation'],
  },
  {
    name: 'Research Analyst',
    desc: 'Runs market research, cleans the data, and writes up what it found.',
    icon: Search, tint: 'bg-sky-50 text-sky-600', cat: 'Operations',
    skills: ['Surveys', 'Data analysis', 'Reporting'],
  },
  {
    name: 'Data Entry Clerk',
    desc: 'Moves records between systems and keeps them consistent.',
    icon: Keyboard, tint: 'bg-orange-50 text-orange-500', cat: 'Operations',
    skills: ['Data entry', 'Validation', 'Sync'],
  },
  {
    name: 'Sales Development Rep',
    desc: 'Qualifies inbound leads, books meetings, and keeps the CRM current.',
    icon: TrendingUp, tint: 'bg-emerald-50 text-emerald-600', cat: 'Sales',
    skills: ['Lead qualification', 'Booking', 'CRM'],
  },
  {
    name: 'Bookkeeper',
    desc: 'Reconciles transactions, chases invoices, and prepares month-end.',
    icon: Calculator, tint: 'bg-rose-50 text-rose-500', cat: 'Finance',
    skills: ['Reconciliation', 'Invoicing', 'Reporting'],
  },
];

const DEPARTMENTS = [
  { name: 'Marketing', desc: 'Content creation, SEO, analytics', icon: Megaphone, tint: 'text-violet-600 bg-violet-50' },
  { name: 'Finance', desc: 'Bookkeeping, reporting, reconciliation', icon: Box, tint: 'text-emerald-600 bg-emerald-50' },
  { name: 'Customer Support', desc: 'Ticket handling, FAQs, follow-ups', icon: MessageCircle, tint: 'text-teal-600 bg-teal-50' },
  { name: 'Operations', desc: 'Data entry, scheduling, reporting', icon: Keyboard, tint: 'text-sky-600 bg-sky-50' },
];

const STATS = [
  { value: '10+', label: 'Hours saved per employee / week' },
  { value: '3.5x', label: 'Increase in team productivity' },
  { value: '70%', label: 'Reduction in time to complete tasks' },
  { value: '$390K+', label: 'Annual labor value created' },
];

/* Department label under each employee name. */
const cat_label = (name) => (EMPLOYEES.find((e) => e.name === name) || {}).cat || '';

const initials = (name) =>
  name
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

/* ---------- page ---------- */

const DemoSiteTeammate = () => {
  const [heroTab, setHeroTab] = useState('Employees');
  const [filter, setFilter] = useState('All');

  const shown = filter === 'All' ? EMPLOYEES : EMPLOYEES.filter((e) => e.cat === filter);

  return (
    <>
      <Breadcrumb trail={['Home', 'Teammate']} />

      {/* Hero */}
      <section className="grid grid-cols-1 items-center gap-12 py-8 lg:grid-cols-2">
        <Hero
          title="Teammate"
          deck={
            <>
              Hire AI employees.
              <br />
              Build your <span className="text-teal-600">AI workforce.</span>
            </>
          }
          actions={
            <>
              <DemoLink />
              <ProductLinks login={PRODUCT_LOGIN.teammate} />
            </>
          }
        >
          A hybrid workforce where AI employees work alongside your team to complete more work and
          scale your business.
        </Hero>

        {/* hero card */}
        <div className={`mx-auto w-full max-w-[400px] rounded-2xl bg-white p-5 ring-1 ring-slate-900/5 ${SHADOW.raised}`}>
          <div className="flex gap-2">
            {['Employees', 'Departments'].map((t) => (
              <button
                key={t}
                onClick={() => setHeroTab(t)}
                className={`rounded-xl px-4 py-2 text-[13px] font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 ${
                  heroTab === t ? 'bg-teal-50 text-teal-700' : 'text-slate-500 hover:bg-slate-50'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="mt-3 divide-y divide-slate-50">
            {HERO_EMPLOYEES.map(({ name, status, tint }) => (
              <div key={name} className="flex items-center gap-3.5 py-3">
                <span className={`flex h-10 w-10 items-center justify-center rounded-full text-[12px] font-bold ${tint}`}>
                  {initials(name)}
                </span>
                <div className="flex-1">
                  <p className="text-[13.5px] font-semibold">{name}</p>
                  <p className={`text-[12px] ${status === 'Active' ? 'text-teal-600' : 'text-slate-500'}`}>{status}</p>
                </div>
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <StatementBand sub="Hire, train and deploy AI employees that work alongside your team — no headcount, no onboarding queue.">
        Build a workforce that <span className="text-teal-600">scales without hiring.</span>
      </StatementBand>

      {/* How it works */}
      <SectionBand tinted title="How Teammate works">
        <div className="mt-8 flex flex-wrap items-start justify-center gap-2 md:gap-3">
          {HOW_STEPS.map(({ n, title, desc }, i) => (
            <React.Fragment key={n}>
              {i > 0 && <ChevronRight size={15} className="mt-3 shrink-0 text-slate-300" />}
              <div className="flex w-[150px] flex-col items-center text-center">
                <StepMarker n={n} />
                <p className="mt-2.5 text-[13.5px] font-semibold leading-tight">{title}</p>
                <p className="mt-1.5 text-[12px] leading-relaxed text-slate-500">{desc}</p>
              </div>
            </React.Fragment>
          ))}
        </div>
      </SectionBand>

      {/* AI employees */}
      <section className={`px-8 py-10 md:px-12 ${SURFACE.band}`}>
        <h2 className={T.h2}>Powerful AI employees</h2>
        <div className="mt-5 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`rounded-xl px-4 py-2 text-[13px] font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 ${
                filter === f ? 'bg-teal-50 text-teal-700' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map(({ name, desc, icon: Icon, tint, skills }) => (
            <div key={name} className={`flex flex-col p-6 ${SURFACE.cardHover}`}>
              <div className="flex items-start gap-3.5">
                <IconTile icon={Icon} tint={tint} />
                <div className="min-w-0">
                  <p className="text-[14.5px] font-semibold leading-tight">{name}</p>
                  <p className={`mt-1 ${T.micro}`}>{cat_label(name)}</p>
                </div>
              </div>
              <p className={`mt-4 flex-1 ${T.body}`}>{desc}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {skills.map((sk) => (
                  <Chip key={sk}>{sk}</Chip>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Departments + chat mockup */}
      <section className={`grid grid-cols-1 items-center gap-10 px-8 py-10 md:px-12 lg:grid-cols-2 ${SURFACE.band}`}>
        <div>
          <h2 className={T.h2}>Built for every department</h2>
          <div className="mt-6 space-y-5">
            {DEPARTMENTS.map(({ name, desc, icon: Icon, tint }) => (
              <div key={name} className="flex items-start gap-3.5">
                <IconTile icon={Icon} tint={tint} size="sm" />
                <div>
                  <p className="text-[14px] font-semibold">{name}</p>
                  <p className="mt-0.5 text-[12.5px] text-slate-500">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={`mx-auto w-full max-w-[400px] rounded-2xl bg-white p-5 ring-1 ring-slate-900/5 ${SHADOW.raised}`}>
          <div className="flex items-center justify-between">
            <p className="text-[14px] font-semibold">Marketing Assistant</p>
            <X size={16} className="text-slate-400" />
          </div>
          <div className="mt-4 flex items-start justify-between gap-3">
            <div className="rounded-2xl rounded-tl-sm bg-violet-50 px-4 py-3 text-[13px] leading-relaxed text-slate-700">
              Create a LinkedIn post for our new product launch.
            </div>
            <div className="flex flex-col gap-2 text-slate-300">
              <HelpCircle size={15} />
              <Lightbulb size={15} />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2.5 rounded-xl border border-slate-200 px-4 py-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-100 text-[10px] font-bold text-violet-600">
              MA
            </span>
            <p className="text-[13px] text-slate-600">Here's a draft for your post!</p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className={`px-6 py-10 md:px-10 ${SURFACE.band}`}>
        <CenterHead title="Real impact. Real results." />
        <div className="mt-8">
          <StatStrip stats={STATS} />
        </div>
      </section>

      <CtaBand
        title="Build your AI workforce today."
        subtitle="Book a demo and we'll show you the work an AI teammate takes off your team."
        primary="Book a demo"
      />
    </>
  );
};

export default DemoSiteTeammate;
